/**
 * E5 -- when something has gone wrong, say who to tell.
 *
 * Walking the admin's gone-wrong journey, not one screen named a
 * person. A connector had failed 588 times, a chain could not be
 * verified, a licence was refused -- and each panel described the
 * fault and stopped. The operator's next action is always the same
 * one (tell somebody) and the product knew who, in Bank Setup's data
 * sources and in the licence claims, and never said.
 *
 * "Copy details" produces a summary somebody can paste into a ticket
 * without going back to the screen to transcribe it -- which is what
 * they would otherwise do, badly.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface EscalationDetails {
  /** What went wrong, in one line. */
  what: string;
  /** Machine-readable context: ids, counts, timestamps. */
  context?: Record<string, string | number | null | undefined>;
}

export function ticketText(
  details: EscalationDetails,
  who: string,
  when: Date = new Date(),
): string {
  const lines = [
    `MARSAD -- ${details.what}`,
    `Observed: ${when.toISOString()}`,
    `Escalate to: ${who}`,
  ];
  for (const [k, v] of Object.entries(details.context ?? {})) {
    if (v === null || v === undefined || v === "") continue;
    lines.push(`${k}: ${v}`);
  }
  return lines.join("\n");
}

export function EscalationLine({
  who,
  details,
  testid = "escalation",
}: {
  /** Who to tell. Named, not "an administrator", wherever we know. */
  who: string;
  details: EscalationDetails;
  testid?: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <p
      data-testid={testid}
      className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-600"
    >
      <span>
        <span className="font-medium">{t("escalate.label")}</span> {who}
      </span>
      <button
        type="button"
        data-testid={`${testid}-copy`}
        onClick={() => {
          const text = ticketText(details, who);
          // Clipboard access can be refused (permissions, insecure
          // context). Say nothing rather than claim a copy that did
          // not happen.
          void navigator.clipboard
            ?.writeText(text)
            .then(() => setCopied(true))
            .catch(() => setCopied(false));
        }}
        className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-medium text-slate-700 hover:border-brand-brass"
      >
        {copied ? t("escalate.copied") : t("escalate.copy")}
      </button>
    </p>
  );
}
